import { defu } from "defu";
import { computed, hasInjectionContext, ref, unref } from "vue";
import type { ComputedRef, Ref, UnwrapNestedRefs, UnwrapRef } from "vue";
import type { Store } from "pinia";
import type {
  GeneratedFetch,
  LocalFetch,
  LocalFetchResponse,
  LocalMutate,
  LocalReactiveFetchHandler,
  PossibleErrorResponses,
  CreateCustomFetchOpts,
  REJECTED_STATUS,
  RESOLVED_STATUS,
} from "../types";
import { argumentParser } from "../utils/argumentParser";
import { ApiRequestOpts, apiRequestOpts, isLocalResolvedResponse, FETCH_STATE, LocalFetchError } from "../utils/api";
import { downloadBlob } from "../utils/fileHandling";
import { useCustomFetch } from "./customFetch";

/**
 * Okay so what we are doing here is we need to extract any union type that isn't null or undefined or never.
 * There is also the possibility that this returns a function somehow? not clear on why that is. BUT we need
 * to find the types where `data` is provided on an object. if that is there, then we will have a return value.
 *
 * So we need to exclude the function, get rid of the nullable types, and then extract where `data: any` exists.
 *
 * Hence, why I am disabling linting the next couple of lines. This is needed here.
 **/
export type FetchResultsData<
  T_FetchMethod extends GeneratedFetch = GeneratedFetch,
  T_Status extends number = number,
> = Extract<
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  Exclude<NonNullable<Awaited<ReturnType<T_FetchMethod>>>, Function>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { status: T_Status; data: any }
>["data"];

/**
 *
 * @param idName - Reserved for future use
 * @param fetchMethod - Oazapfts generated fetch handler
 * @param customFetchOpts - Custom fetch options for extra handlers
 */
export function useLocalFetch<
  T_FetchMethod extends GeneratedFetch = GeneratedFetch,
  T_FetchType extends FetchResultsData<T_FetchMethod, RESOLVED_STATUS> = FetchResultsData<
    T_FetchMethod,
    RESOLVED_STATUS
  >,
  T_FetchTypeError extends FetchResultsData<T_FetchMethod, REJECTED_STATUS> = FetchResultsData<
    T_FetchMethod,
    REJECTED_STATUS
  >,
>(
  idName: string,
  fetchMethod: T_FetchMethod,
  customFetchOpts?: CreateCustomFetchOpts,
): LocalReactiveFetchHandler<
  T_FetchType,
  T_FetchTypeError,
  Parameters<T_FetchMethod>,
  GeneratedFetch<T_FetchType, T_FetchTypeError, Parameters<T_FetchMethod>>,
  LocalFetchResponse<T_FetchType, T_FetchTypeError>
> {
  if (!hasInjectionContext()) {
    devConsole.error(`useLocalFetch("${idName}, ${fetchMethod.name}") Called Improperly!`);
    devConsole.error(`please only call useLocalFetch in the \`setup\` Context of a component or store!`);
    devConsole.groupCollapsed(`stackTrace`);
    devConsole.trace();
    devConsole.groupEnd();
  }

  type T_FetchResponse = LocalFetchResponse<T_FetchType, PossibleErrorResponses | T_FetchTypeError>;

  type T_RefResponse = Ref<UnwrapRef<T_FetchResponse> | T_FetchResponse>;

  // Fetch methods are generated by Oazapfts. We need to check if there are optional parameters with a
  // set default. For those methods, if we don't add undefined before the ApiRequestOpts object, it will
  // set the ApiRequestOpts object properties as parameters.
  const argString = argumentParser(fetchMethod);
  const createEmptyResponse = (): T_FetchResponse => ({ status: 0, headers: {}, blob: new Blob() }) as T_FetchResponse;

  const __customFetch = useCustomFetch(customFetchOpts);
  const {
    apiUrl,
    $i18n: { t },
    showPending,
    getNotificationError: getError,
  } = useFetchContext();

  const fetchState: Ref<FETCH_STATE> = ref(FETCH_STATE.INITIAL);
  const response: T_RefResponse = ref<T_FetchResponse>(createEmptyResponse());
  const promise: Ref<Promise<T_FetchResponse>> = ref(Promise.resolve(createEmptyResponse()));
  const errors = computed<null | LocalFetchError<T_FetchTypeError>>(() =>
    !isLocalResolvedResponse(response.value) ? (response.value.data as LocalFetchError<T_FetchTypeError>) : null,
  );
  const data = computed<T_FetchType | null>(() =>
    isLocalResolvedResponse(response.value) ? (response.value.data as T_FetchType) : null,
  );
  const blob = computed<Blob>(() => response.value.blob ?? new Blob());
  const status = computed<number>(() => response.value.status || 0);
  const pending = computed(() => {
    return fetchState.value === FETCH_STATE.PENDING;
  }) as ComputedRef<false> | ComputedRef<true>;
  const resolved = computed<false | true>(() => {
    return fetchState.value === FETCH_STATE.RESOLVED;
  }) as ComputedRef<false> | ComputedRef<true>;
  const rejected = computed<false | true>(() => {
    return fetchState.value === FETCH_STATE.REJECTED;
  }) as ComputedRef<false> | ComputedRef<true>;
  const initial = computed<false | true>(() => {
    return fetchState.value === FETCH_STATE.INITIAL;
  }) as ComputedRef<false> | ComputedRef<true>;
  const reset = () => {
    response.value = createEmptyResponse();
    promise.value = Promise.resolve(createEmptyResponse());
    fetchState.value = FETCH_STATE.INITIAL;
  };

  const fetch: LocalFetch<T_FetchType, PossibleErrorResponses, Parameters<T_FetchMethod>> = async (...args) => {
    fetchState.value = FETCH_STATE.PENDING;
    if (argString.length > 1 && args.length === 0) {
      args.push(undefined);
    }
    const argLength = args?.length;
    let opts = args.pop();

    if (opts instanceof ApiRequestOpts) {
      opts.fetch = __customFetch;
      opts.baseUrl = apiUrl.value;
    } else {
      // if there are no args, we don't need to put our popped undefined arg back in the array
      if (argLength > 0) args.push(opts);
      opts = apiRequestOpts({
        baseUrl: apiUrl.value,
        fetch: __customFetch,
      });
    }

    args.push(opts);
    // eslint-disable-next-line no-useless-assignment
    let __response: T_FetchResponse = {
      status: 600,
      headers: new Headers(),
      data: new LocalFetchError("error", "This should not be seen.", 0),
    };
    try {
      promise.value = fetchMethod(...args);
      __response = await promise.value;
      __response.blob = await __customFetch.meta.response.clone().blob();
      if (__response.status === 0) {
        __response.data = new LocalFetchError("error", "CORS Error", 0);
      }
      if (__response.status >= 400) {
        fetchState.value = FETCH_STATE.REJECTED;
        try {
          const r = __customFetch.meta.response.clone();
          __response.data = new LocalFetchError("json", await r.json(), <REJECTED_STATUS>response.value.status);
        } catch (e) {
          if (e instanceof TypeError || e instanceof SyntaxError) {
            const r = __customFetch.meta.response.clone();
            __response.data = new LocalFetchError("text", await r.text(), <REJECTED_STATUS>response.value.status);
          } else {
            throw new Error("Issue deserializing response data", { cause: e });
          }
        }
      } else {
        fetchState.value = FETCH_STATE.RESOLVED;
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        devConsole.log("Rejected!");
        devConsole.dir(e);
      }
      fetchState.value = FETCH_STATE.REJECTED;
      __response = {
        status: 600,
        headers: new Headers(),
        data: new LocalFetchError("error", e as Error, 600, e as Error),
        blob: new Blob(),
      };
    }

    if (import.meta.env.VITEST) {
      if (__response.status >= 500 && __response.data instanceof LocalFetchError) {
        throw new Error(
          `Api "${__customFetch.meta.request.url}" (${fetchMethod.name}) returned ${__response.status} code with message: "${JSON.stringify(__response.data.body, null, 2)}"`,
        );
      }
    }
    // put a fresh copy here, headers cannot be cloned. Why? ask TC39.
    const { headers, ...theRest } = __response;
    response.value = { ...structuredClone(theRest), headers: new Headers(headers) };
    // return our original object so we can get better async responses
    if (theRest.data instanceof LocalFetchError) {
      response.value.data = theRest.data;
    }
    return __response;
  };

  Object.defineProperties(fetch, {
    name: {
      get(): string {
        return fetchMethod.name;
      },
    },
    toString: {
      get(): () => string {
        return () => fetchMethod.toString();
      },
    },
    length: {
      get(): number {
        return fetchMethod.length;
      },
    },
  });

  const download = async (
    filename: string = "download",
    blobOptions: BlobPropertyBag = { type: "application/octet-stream" },
    ...fetchArgs: Parameters<typeof fetchMethod>
  ) => {
    const {
      begin: beginMessage,
      ready: readyMessage,
      ...errorMessages
    } = defu(customFetchOpts?.downloadErrorCodeMessages, {
      begin: "desc.downloadBegin",
      ready: "desc.downloadReady",
      404: "desc.download404",
    });

    const messageHandle = showPending(t(beginMessage, [filename]));
    await fetch(...fetchArgs);
    if (resolved.value) {
      let extractedName = response.value.headers.get("content-disposition");
      if (extractedName) {
        const results = /attachment; filename="?(?<ascii>.+)"?; filename\*=UTF-8''(?<utf8>.+)/.exec(extractedName);
        devConsole.log(results?.groups);
        try {
          if (results?.groups?.ascii) {
            extractedName = decodeURIComponent(results.groups.ascii);
          }
          if (results?.groups?.utf8) {
            extractedName = decodeURIComponent(results.groups.utf8);
          }
        } catch {
          // in the event something goes wrong with decoding the uri string,
          // this will cause the download to fall back to `filename`
          extractedName = null;
        }
      }
      extractedName = extractedName ?? filename ?? "download";
      const file = blob.value;
      messageHandle
        .setFile(file, extractedName)
        .then($ck.utils.DO_NOTHING)
        .catch((e: Error) => {
          devConsole.log(e);
          messageHandle.close();
        });
      messageHandle.setTimeout($ck.utils.TimeInMS.FIVE_SECONDS);
      messageHandle.setType($ck.utils.NotificationType.Success);
      messageHandle.setMessage(t(readyMessage, [extractedName]));
      downloadBlob(file, extractedName, blobOptions);
    } else {
      messageHandle.setTimeout($ck.utils.TimeInMS.TEN_SECONDS);
      messageHandle.setType($ck.utils.NotificationType.Error);
      if (status.value in errorMessages) {
        messageHandle.setMessage(t(errorMessages[status.value], [JSON.stringify(errors.value)]));
      } else {
        messageHandle.setMessage(getError(errors.value!));
      }
    }
    return response.value;
  };

  const mutate: LocalMutate<T_FetchType, T_FetchTypeError, Parameters<T_FetchMethod>> = async (...fetchArgs) => {
    const result = await fetch(...fetchArgs);

    if (isLocalResolvedResponse(result)) {
      return {
        resolved: true,
        rejected: false,
        errors: null,
        data: $ck.utils.vueStructuredClone(result.data as T_FetchType),
        status: result.status as RESOLVED_STATUS,
        headers: result.headers,
      };
    }

    return {
      resolved: false,
      rejected: true,
      errors: (result.data as LocalFetchError<T_FetchTypeError>) ?? null,
      data: null,
      status: result.status as REJECTED_STATUS,
      headers: result.headers,
    };
  };

  return {
    name: idName,
    fetchState,
    response,
    promise,
    data,
    blob,
    errors,
    status,
    pending,
    initial,
    resolved,
    rejected,
    reset,
    fetch,
    download,
    mutate,
  } as LocalReactiveFetchHandler<
    T_FetchType,
    T_FetchTypeError,
    Parameters<T_FetchMethod>,
    GeneratedFetch<T_FetchType, T_FetchTypeError, Parameters<T_FetchMethod>>,
    LocalFetchResponse<T_FetchType, T_FetchTypeError>
  >;
}

export type FetchHandler<
  T,
  T_Err extends PossibleErrorResponses = PossibleErrorResponses,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Args extends any[] = any[],
> = LocalReactiveFetchHandler<T, T_Err, Args, GeneratedFetch<T, T_Err, Args>, LocalFetchResponse<T, T_Err>>;

export type ReactiveFetchHandler<T, T_Err extends PossibleErrorResponses = PossibleErrorResponses> = UnwrapNestedRefs<
  FetchHandler<T, T_Err>
>;

export type PiniaFetchHandler<
  T,
  T_Err extends PossibleErrorResponses = PossibleErrorResponses,
  T_Handler extends FetchHandler<T, T_Err> = FetchHandler<T, T_Err>,
> = Store<
  string,
  Pick<T_Handler, "promise" | "fetchState" | "response" | "name">,
  Pick<T_Handler, "initial" | "resolved" | "rejected" | "pending" | "errors" | "status" | "blob" | "data">,
  Pick<T_Handler, "fetch" | "download" | "reset">
>;

/**
 * To be used when we care about the shape of the fetch handler, but do not care about the actual result.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PassedFetchHandler<T = any, T_Err extends PossibleErrorResponses = PossibleErrorResponses> =
  FetchHandler<T, T_Err> | ReactiveFetchHandler<T, T_Err> | PiniaFetchHandler<T, T_Err>;

// This type is for testing purposes.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ResolvedRequest<T = any> = { status: RESOLVED_STATUS; headers: Headers; data: T };

export type CachedFetchOptions = {
  maxCalledTimes?: number;
  maxCachedTime?: number;
};

export function useCachedFetch<T, T_Err extends PossibleErrorResponses>(
  originalFetch: GeneratedFetch<T, T_Err>,
  promise: Ref<Promise<LocalFetchResponse<T, T_Err>>>,
  options: CachedFetchOptions = {},
) {
  if (argumentParser(originalFetch).length > 1) {
    devConsole.error(
      `You cannot use ${originalFetch.name} with useCachedFetch. It has parameters that would not get invalidated on change.`,
    );
  }
  const opts = defu<Required<CachedFetchOptions>, CachedFetchOptions[]>(options, {
    maxCalledTimes: 30,
    maxCachedTime: $ck.utils.TimeInMS.FIVE_MINUTES,
  });

  const cacheTime = ref(0);
  const calledTimes = ref(0);

  const voidCache = () => {
    cacheTime.value = 0;
    calledTimes.value = 0;
  };

  const cachedFetch = async (...args: Parameters<typeof originalFetch>): ReturnType<typeof originalFetch> => {
    // if we have fetched between 0 and 30 times since the last call, and it has been less than 5 minutes
    // just return the previous value. This call is going to be lightly cached in memory, so that multiple calls
    // will not immediately re-fetch this list.
    if (
      Date.now() - cacheTime.value < opts.maxCachedTime &&
      calledTimes.value <= opts.maxCalledTimes &&
      calledTimes.value > 0
    ) {
      calledTimes.value++;
      return promise.value;
    }

    calledTimes.value = 1;
    cacheTime.value = Date.now();

    return originalFetch(...args);
  };

  return {
    voidCache,
    cachedFetch,
  };
}

export function useIsReady<
  T,
  T_Err extends PossibleErrorResponses = PossibleErrorResponses,
  T_FetchHandler extends PassedFetchHandler<T, T_Err> = PassedFetchHandler<T, T_Err>,
>(handler: T_FetchHandler) {
  const { $auth } = useFetchContext();

  return async function isLocalFetchReady(...args: Parameters<T_FetchHandler["fetch"]>) {
    const authed = await $auth.isAuthed();
    if (authed) {
      /**
       * JS Event loop is weird, and async will get things out of sync. So we're gonna need this.
       * @private
       */
      let __promise: Promise<LocalFetchResponse<T, PossibleErrorResponses | T_Err>>;
      if (!unref(handler.initial)) {
        // do the await this way so if we have already started, then it will listen to the promise;
        __promise = unref(handler.promise);
      } else {
        // Store this as a promise here since the promise might not get set before being called.
        const fetchMethod = (
          "safeMutate" in handler ? handler.safeMutate : "safeFetch" in handler ? handler.safeFetch : handler.fetch
        ) as typeof handler.fetch;

        __promise = fetchMethod(...args);
      }
      await __promise;
      // we're going to wait for pending to not be the current state anymore.
      // There is something about the vue/pinia microtask queue that will prevent this from updating quickly enough in async,
      // so we have to wait for it to change ourselves. This will max out at 50ms (5ms timeout, 10 tries).
      await $ck.utils.createWaitForPromise(() => !unref(handler.pending));
      if (unref(handler.resolved)) {
        // now we are good to go.
        return true;
      }
    }
    return false;
  };
}
